import { isAxiosError } from "axios";
import { apiUtils } from "../../../utils/apiUtils";
import type {
  MerchantKeyInsightsResponse,
  MerchantRedemptionsTrendResponse,
  MerchantStatisticsResponse,
  MerchantTopCouponsResponse,
} from "../types/merchant-home-data.types";

export type {
  MerchantKeyInsightsResponse,
  MerchantRedemptionsTrendResponse,
  MerchantStatisticsResponse,
  MerchantTopCouponsResponse,
} from "../types/merchant-home-data.types";

export interface MerchantHomeRequestOptions {
  signal?: AbortSignal;
}

const getResponseMessage = (value: unknown, fallback: string): string => {
  if (typeof value !== "object" || value === null) {
    return fallback;
  }

  const record = value as Record<string, unknown>;

  return typeof record.user_message === "string"
    ? record.user_message
    : typeof record.message === "string"
      ? record.message
      : fallback;
};

export const getMerchantHomeErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError(error)) {
    return getResponseMessage(error.response?.data, error.message || fallback);
  }

  return error instanceof Error ? error.message : fallback;
};

export async function getMerchantStatistics(
  options?: MerchantHomeRequestOptions,
): Promise<MerchantStatisticsResponse> {
  return apiUtils.getData<MerchantStatisticsResponse>(
    "/v1/merchant/home/statistic",
    "Failed to fetch merchant statistics",
    { signal: options?.signal },
  );
}

export async function getMerchantRedemptionsTrend(
  options?: MerchantHomeRequestOptions,
): Promise<MerchantRedemptionsTrendResponse> {
  return apiUtils.getData<MerchantRedemptionsTrendResponse>(
    "/v1/merchant/home/redemptions-trend",
    "Failed to fetch redemptions trend",
    { signal: options?.signal },
  );
}

export async function getMerchantKeyInsights(
  options?: MerchantHomeRequestOptions,
): Promise<MerchantKeyInsightsResponse> {
  return apiUtils.getData<MerchantKeyInsightsResponse>(
    "/v1/merchant/home/key-insights",
    "Failed to fetch key insights",
    { signal: options?.signal },
  );
}

export async function getMerchantTopCoupons(
  options?: MerchantHomeRequestOptions,
): Promise<MerchantTopCouponsResponse> {
  return apiUtils.getData<MerchantTopCouponsResponse>(
    "/v1/merchant/home/top-performing-coupons",
    "Failed to fetch top performing coupons",
    { signal: options?.signal },
  );
}
